import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Coordinate } from 'ol/coordinate';
import useAppState from '../../../AppStore';
import { isVisibleUpToRoot } from '../../mapContent/api/mapUtilities';
import { LayerIdentifyResponse } from '../api/contract';
import IdentifyMessageBox from './IdentifyMessageBox';
import Accordion from './Accordion';
import AccordionItem from './AccordionItem';
import IdentifyLayerInfoResults from './IdentifyLayerInfoResults';
import useIdentifyLayerinfo, { IdentifyMapserviceProps } from '../api/useIdentify';
import useSubLayersSetting from '../../mapContent/api/useSubLayersSetting';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import IdentifyLayerInfoPlaceholder from './IdentifyLayerInfoPlaceholder';
import { AccordionName } from '../../../UIStateSlice';

interface IdentifyLayerInfoProps {
  topicLayerId: string;
  url: string;
  isPublic: boolean;
  layerType: string;
  title: string;
  identifyCoordinate: Coordinate;
  tolerance: number;
}

const IdentifyLayerInfo = ({
  topicLayerId,
  url,
  isPublic,
  layerType,
  title,
  identifyCoordinate,
  tolerance,
}: IdentifyLayerInfoProps) => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const accordions = useAppState((state) => state.accordions);
  const setAccordionItem = useAppState((state) => state.setAccordionItem);

  const mapLocation = useAppState((state) => state.location);

  const mapLayerSetting = useSubLayersSetting(topicLayerId, url, isPublic);
  const layerSetting = mapLayerSetting.data ?? {};

  const subLayersSetting = Object.values(layerSetting).filter((layer) => layer.isVisible);
  const visibleSublayers = subLayersSetting
    .filter(
      (item) =>
        isVisibleUpToRoot(item.id, subLayersSetting, mapLocation.resolution) &&
        !item.isGroupLayer,
    )
    .map((item) => item.id);

  const topicLayerProps: IdentifyMapserviceProps = {
    topicLayerId,
    isPublic,
    layerType,
    visibleSublayers,
  };

  const {
    isLoading,
    isError,
    data: identifyResult,
    error,
  } = useIdentifyLayerinfo(identifyCoordinate, tolerance, topicLayerProps);

  useEffect(() => {
    if (isError) {
      Tracking.Error.identifyLayerInfoError(topicLayerId, error.message);
    }
  }, [isError]);

  const noResultFound = (response: LayerIdentifyResponse | undefined) =>
    !response?.results?.some((res) => res.items.length > 0);

  useEffect(() => {
    if (isError || noResultFound(identifyResult)) {
      const id = `${topicLayerId}_0`;
      const exist = !!accordions[AccordionName.IDENTIFY_CONFIGURATION]?.find((i) => {
        return i.id === id;
      });
      if (!exist) setAccordionItem(AccordionName.IDENTIFY_CONFIGURATION, id, true);
    }
  }, [identifyResult, isError]);

  if (isLoading) {
    return <IdentifyLayerInfoPlaceholder />;
  }

  if (isError) {
    return (
      <Accordion>
        <AccordionItem
          title={title}
          key={`${topicLayerId}_0`}
          id={`${topicLayerId}_0`}
          onToggle={(state) => {
            Tracking.Identify.accordionToggle('error', state ? 'closed' : 'open');
          }}
        >
          <IdentifyMessageBox message={t('identify_error_message')} type="negative" />
        </AccordionItem>
      </Accordion>
    );
  }

  if (noResultFound(identifyResult)) {
    return (
      <Accordion>
        <AccordionItem
          title={title}
          key={`${topicLayerId}_0`}
          id={`${topicLayerId}_0`}
          onToggle={(state) => {
            Tracking.Identify.accordionToggle('noResult', state ? 'closed' : 'open');
          }}
        >
          <IdentifyMessageBox message={t('identify_noinfo_message')} type="information" />
        </AccordionItem>
      </Accordion>
    );
  }

  return <IdentifyLayerInfoResults identifyResponse={identifyResult} />;
};

export default IdentifyLayerInfo;
